// 補助金不正防止モジュールの判定ロジック — 純粋関数（副作用なし）。
// 仕様の単一実装: フロント / API / テストが同じ判定を共有する。
//
// ここでは次を判定する:
//   - 有効リード判定 (evaluateValidLead)        ... 仕様3
//   - アポイント取得判定 (evaluateAppointment)   ... 仕様4
//   - リスクスコア 0-100 (computeRiskScore)      ... 仕様10
//   - 支払ブロック理由 (paymentBlockReasons)     ... 仕様9
//
// 重要: このシステムは「証拠を作る」ためではなく、実在する業務実態を記録・確認する
// ためのもの。実態のない支払い・架空紹介・自己負担補填・還流に見える取引は止める。

// ---------------------------------------------------------------------------
// 小ヘルパ（日付・金額の近接判定）— Date.now() は使わない（与えられた値のみ）
// ---------------------------------------------------------------------------
function s(v: string | null | undefined): string {
  return (v ?? "").trim();
}

/** 2つの日付(YYYY-MM-DD...)の差を日数で返す。どちらか欠ければ null。 */
export function daysBetween(a?: string | null, b?: string | null): number | null {
  const da = s(a).slice(0, 10);
  const db = s(b).slice(0, 10);
  if (!da || !db) return null;
  const ta = Date.parse(da);
  const tb = Date.parse(db);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return null;
  return Math.round((ta - tb) / 86400000);
}

/** 支払時期がC補助金入金日に近いか（既定±14日以内）。 */
export function isDateNear(a?: string | null, b?: string | null, withinDays = 14): boolean {
  const d = daysBetween(a, b);
  return d != null && Math.abs(d) <= withinDays;
}

/** 支払額がCの自己負担額に近いか（既定: 差が自己負担額の15%以内、かつ双方が正）。 */
export function isAmountNearSelfPay(amount?: number | null, selfPay?: number | null): boolean {
  const amt = Number(amount ?? 0);
  const self = Number(selfPay ?? 0);
  if (amt <= 0 || self <= 0) return false;
  return Math.abs(amt - self) / self <= 0.15;
}

// ---------------------------------------------------------------------------
// 仕様3: 有効リード判定
// ---------------------------------------------------------------------------
export interface ValidLeadInput {
  companyName?: string | null;
  storeName?: string | null;
  address?: string | null;
  representative?: string | null;
  phone?: string | null;
  email?: string | null;
  referralDate?: string | null;
  referralMethod?: string | null;
  hasConsent: boolean;                 // DがA/Bから連絡を受けることに同意した記録
  contactedAt?: string | null;         // B/AがDへ実際に連絡した記録
  duplicateStatus: "unchecked" | "unique" | "duplicate";
  antisocialChecked: boolean;          // 反社確認済み
  existenceChecked: boolean;           // 実在確認済み
  sameAsC: boolean;                    // C自身/実質同一/関係会社
}

export interface RuleResult {
  ok: boolean;
  missing: string[]; // 満たしていない条件（不足証拠チェックリスト用）
}

export function evaluateValidLead(i: ValidLeadInput): RuleResult {
  const missing: string[] = [];
  // 単なる「名前と電話番号」だけでは有効リードにしない → 実在 + 複数項目を要求
  if (!i.existenceChecked) missing.push("紹介先の実在確認");
  if (!s(i.companyName)) missing.push("会社名/屋号");
  if (!s(i.address)) missing.push("所在地");
  if (!s(i.representative)) missing.push("代表者/担当者名");
  if (!s(i.phone) && !s(i.email)) missing.push("連絡先（電話またはメール）");
  if (!s(i.referralDate)) missing.push("紹介日");
  if (!s(i.referralMethod)) missing.push("紹介方法");
  if (!i.hasConsent) missing.push("紹介先の連絡同意記録");
  if (!s(i.contactedAt)) missing.push("自社が紹介先へ連絡した記録");
  if (i.duplicateStatus === "duplicate") missing.push("重複リードでないこと（現在: 重複）");
  if (!i.antisocialChecked) missing.push("反社確認");
  if (i.sameAsC) missing.push("紹介元（顧客）と実質同一でないこと（現在: 同一の疑い）");
  return { ok: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------
// 仕様4: アポイント取得判定
// ---------------------------------------------------------------------------
export interface AppointmentInput {
  validLead: boolean;                  // 有効リード条件を満たしているか
  appointAt?: string | null;           // アポイント日時
  place?: string | null;               // 場所 または オンラインURL
  participants?: string | null;
  dContact?: string | null;            // D側担当者
  bConfirmed: boolean;                 // B/Aが商談予定を確認
  status: "scheduled" | "done" | "canceled";
  hasNegotiation: boolean;             // 商談実施記録
}

export function evaluateAppointment(i: AppointmentInput): RuleResult {
  const missing: string[] = [];
  if (!i.validLead) missing.push("有効リード条件（未達）");
  if (!s(i.appointAt)) missing.push("アポイント日時");
  if (!s(i.place)) missing.push("場所またはオンラインURL");
  if (!s(i.participants)) missing.push("参加者");
  if (!s(i.dContact)) missing.push("紹介先側の担当者");
  if (!i.bConfirmed) missing.push("自社の商談予定確認");
  // 実際に商談した(done)場合は商談実施記録が必須
  if (i.status === "done" && !i.hasNegotiation) missing.push("商談実施記録");
  return { ok: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------
// 仕様10: リスクスコアリング 0-100
// ---------------------------------------------------------------------------
export type RiskBand = "low" | "review" | "high" | "block";

export const RISK_BANDS: { band: RiskBand; min: number; max: number; label: string }[] = [
  { band: "low", min: 0, max: 29, label: "低リスク" },
  { band: "review", min: 30, max: 59, label: "要確認" },
  { band: "high", min: 60, max: 79, label: "高リスク" },
  { band: "block", min: 80, max: 100, label: "支払不可" },
];

export function riskBand(score: number): RiskBand {
  const found = RISK_BANDS.find((b) => score >= b.min && score <= b.max);
  return found?.band ?? "block";
}

export function riskBandLabel(band: RiskBand): string {
  return RISK_BANDS.find((b) => b.band === band)?.label ?? "支払不可";
}

export interface RiskInput {
  hasConsent: boolean;                 // D同意記録あり
  hasNegotiation: boolean;             // D商談記録あり
  existenceChecked: boolean;           // D実在確認あり
  isDuplicate: boolean;                // Dが既存/重複リード
  relatedToC: boolean;                 // DがCの関係者/関連会社の可能性
  paymentAmount?: number | null;
  selfPayAmount?: number | null;       // Cの自己負担額
  paymentDate?: string | null;
  subsidyPaidDate?: string | null;     // C補助金入金日
  invoiceSubjectReferralOnly: boolean; // 請求名目が「紹介料」のみ
  hasContract: boolean;
  hasInvoice: boolean;
  hasApproval: boolean;                // 支払承認(承認済み)あり
  appointmentDone: boolean;            // アポ実施済み
  lostReasonMissing: boolean;          // 失注なのに理由未記録
  memoLinksSubsidy: boolean;           // C補助金案件と紐づくようなメモ
  conditionedOnSubsidy: boolean;       // 補助金受給を条件とする記録
}

export interface RiskFactor {
  code: string;
  label: string;
  points: number;
}

// 各リスク加点項目（仕様10）。
const RISK_RULES: { code: string; label: string; points: number; hit: (i: RiskInput) => boolean }[] = [
  { code: "no_consent", label: "紹介先の同意記録なし", points: 10, hit: (i) => !i.hasConsent },
  { code: "no_negotiation", label: "紹介先の商談記録なし", points: 10, hit: (i) => !i.hasNegotiation },
  { code: "no_existence", label: "紹介先の実在確認なし", points: 10, hit: (i) => !i.existenceChecked },
  { code: "duplicate", label: "紹介先が既存リード（重複）", points: 15, hit: (i) => i.isDuplicate },
  { code: "related_to_c", label: "紹介先が紹介元（顧客）の関係者・関連会社の可能性", points: 20, hit: (i) => i.relatedToC },
  { code: "amount_near_selfpay", label: "支払額が紹介元（顧客）の自己負担額に近い", points: 20, hit: (i) => isAmountNearSelfPay(i.paymentAmount, i.selfPayAmount) },
  { code: "time_near_subsidy", label: "支払時期が紹介元（顧客）の補助金入金と近い", points: 15, hit: (i) => isDateNear(i.paymentDate, i.subsidyPaidDate) },
  { code: "referral_only", label: "請求名目が「紹介料」のみ", points: 10, hit: (i) => i.invoiceSubjectReferralOnly },
  { code: "no_contract", label: "契約書なし", points: 10, hit: (i) => !i.hasContract },
  { code: "no_invoice", label: "請求書なし", points: 10, hit: (i) => !i.hasInvoice },
  { code: "no_approval", label: "支払承認なし", points: 10, hit: (i) => !i.hasApproval },
  { code: "no_appointment", label: "アポ未実施", points: 5, hit: (i) => !i.appointmentDone },
  { code: "lost_reason_missing", label: "失注理由未記録", points: 5, hit: (i) => i.lostReasonMissing },
  { code: "memo_links_subsidy", label: "紹介元（顧客）の補助金案件と紐づくメモがある", points: 20, hit: (i) => i.memoLinksSubsidy },
  { code: "conditioned_on_subsidy", label: "補助金受給を条件とする記録がある", points: 25, hit: (i) => i.conditionedOnSubsidy },
];

export interface RiskResult {
  score: number; // 0-100
  band: RiskBand;
  bandLabel: string;
  factors: RiskFactor[];
}

export function computeRiskScore(input: RiskInput): RiskResult {
  const factors: RiskFactor[] = [];
  let raw = 0;
  for (const r of RISK_RULES) {
    if (r.hit(input)) {
      factors.push({ code: r.code, label: r.label, points: r.points });
      raw += r.points;
    }
  }
  const score = Math.min(100, raw);
  const band = riskBand(score);
  return { score, band, bandLabel: riskBandLabel(band), factors };
}

// ---------------------------------------------------------------------------
// 仕様9: 支払ブロック条件（BからCへの支払い）
// ---------------------------------------------------------------------------
export interface PaymentBlockInput {
  hasContract: boolean;                // 契約書登録
  hasInvoice: boolean;                 // 請求書登録
  targetLeadCount: number;             // 対象D一覧の件数
  validLeadCount: number;              // 有効リード条件を満たすDの件数
  anyDuplicate: boolean;               // 重複リードを含む
  anyMissingConsent: boolean;          // 同意のないDを含む
  anyMissingExistence: boolean;        // 実在確認のないDを含む
  antisocialAllChecked: boolean;       // 全Dの反社確認完了
  approvalApproved: boolean;           // 支払承認書が承認済み
  amountNearSelfPay: boolean;          // 自己負担補填に見える
  riskBand: RiskBand;                  // リスク判定
  sameAsCAny: boolean;                 // C実質同一の疑いを含む
}

export function paymentBlockReasons(i: PaymentBlockInput): string[] {
  const reasons: string[] = [];
  if (!i.hasContract) reasons.push("契約書が未登録");
  if (!i.hasInvoice) reasons.push("請求書が未登録");
  if (i.targetLeadCount <= 0) reasons.push("対象の紹介先が未登録");
  if (i.validLeadCount <= 0) reasons.push("有効リード条件を満たす紹介先が存在しない");
  if (i.anyMissingConsent) reasons.push("紹介先の連絡同意がない");
  if (i.anyMissingExistence) reasons.push("紹介先の実在確認がない");
  if (i.anyDuplicate) reasons.push("重複リードが含まれている");
  if (i.sameAsCAny) reasons.push("架空または疑義あり（紹介元と実質同一の疑い）");
  if (i.amountNearSelfPay) reasons.push("紹介元（顧客）の補助金自己負担補填に見える（支払額が自己負担額に近い）");
  if (i.riskBand === "high" || i.riskBand === "block") reasons.push("還流・キャッシュバックの疑いが高い（リスク高）");
  if (!i.approvalApproved) reasons.push("支払承認書が未承認");
  if (!i.antisocialAllChecked) reasons.push("反社確認が未完了");
  return reasons;
}

/** 支払可能か（ブロック理由が0件なら可）。 */
export function isPaymentAllowed(i: PaymentBlockInput): boolean {
  return paymentBlockReasons(i).length === 0;
}

// 請求書の件名が「紹介料」のみか（補助金不正例に近く見える）→ 警告用。
export function isRiskyInvoiceSubject(subject?: string | null): boolean {
  return subject === "referral_fee";
}

export const RISKY_INVOICE_SUBJECT_WARNING =
  "この件名は補助金不正例に近く見える可能性があります。実態が有効リード提供業務または" +
  "アポイント取得支援業務である場合は、実態に合った請求名目を使用してください。";
